import { useEffect } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useIsSignedIn } from "@empite/session";
import type { SessionInfo } from "@empite/api-client";
import { isApiError } from "@empite/api-client";
import { Button } from "../ui/button";
import { Card, CardBody, CardHeader } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { currentReturnPath } from "../oauth";

/** The registration states that may spend, mirroring RegistrationState.CanSpend. */
const SPENDABLE_STATES = ["phone_verified", "account_active"];

export function canSpend(session: SessionInfo | null): boolean {
  return session !== null && SPENDABLE_STATES.includes(session.registration_state);
}

/**
 * RequireSession renders its children only for a signed-in visitor, and
 * sends everyone else to sign in with a way back.
 *
 * The `ready` check is the part that matters: `signedIn` is false during the
 * first session fetch too, so redirecting on `!signedIn` alone bounces a
 * signed-in customer to the sign-in screen on every hard refresh.
 */
export function RequireSession({ children }: { children: (session: SessionInfo) => ReactNode }) {
  const { ready, signedIn, session } = useIsSignedIn();
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && !signedIn) {
      void navigate({ to: "/sign-in", search: { next: currentReturnPath() }, replace: true });
    }
  }, [ready, signedIn, navigate]);

  if (!ready) {
    // A skeleton rather than a spinner: every ported screen already reports its
    // own pending state this way, and the session check is the frame around
    // them — two different loading idioms stacked would read as two waits.
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Checking your session">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full rounded-card" />
      </div>
    );
  }
  if (!session) {
    return (
      <Card>
        <CardHeader
          title="Sign in to continue"
          description="This page is part of your account."
        />
        <CardBody>
          <Button href="/sign-in" search={{ next: currentReturnPath() }}>
            Sign in
          </Button>
        </CardBody>
      </Card>
    );
  }
  return <>{children(session)}</>;
}

/** The human-facing text for anything thrown by the client. */
export function errorMessage(err: unknown, fallback = "Something went wrong. Try again."): string {
  if (isApiError(err)) {
    return err.message;
  }
  if (err instanceof Error && err.message !== "") {
    return err.message;
  }
  return fallback;
}
